import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import SumoLogicConnectForm from "./SumoLogicConnectForm";

const INTEGRATION_CODE_NAME = "sumologic";

const SumoLogicIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerSumoLogicIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerSumoLogicIntegration(values.webhookURL.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integrated successfully`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} webhook.`
        }
        image={logos.SumoLogicLogo}
        iconBorderRadius={0}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <WebhookConfig
              toolName={integration.codeName}
              toolDisplayName={integration.displayName}
            />
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <GuidelineStepByStep>
                <GuidelineStep title="Navigate to Sumo Logic">
                  <Typography variant="body2">
                    Open your Sumo Logic account and sign in.
                  </Typography>
                  <Box style={{ display: "flex", marginTop: "10px" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://service.sumologic.com/ui/",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none" }}
                    >
                      Sign in to Sumo Logic
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create new webhook connection">
                  <Typography variant="body2">
                    1. Navigate to <strong>Manage Data</strong> →{" "}
                    <strong>Connections</strong>
                  </Typography>
                  <Typography variant="body2">
                    2. Click <strong>+ Add Connection</strong>
                  </Typography>
                  <Typography variant="body2">
                    3. Select <strong>Webhook</strong> as the connection type
                  </Typography>
                  <Typography variant="body2">
                    4. Configure your webhook and copy the{" "}
                    <strong>Webhook URL</strong>
                  </Typography>
                  <Typography variant="body2">
                    5. Paste the URL in the input field below
                  </Typography>
                </GuidelineStep>
                <GuidelineStep id="connectSumoLogic" title="Connect Sumo Logic">
                  <Box width={700}>
                    <SumoLogicConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default SumoLogicIntegration;
